import { MessageSquareIcon } from "lucide-react";
import type { ComponentProps } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/*
 * The app's one way into a discussion, wherever comments open: a record
 * table's row, a review queue's row, a canvas. A speech bubble with its
 * comments counted on a small badge once there are any; where it opens the
 * discussion beside the page, it stays pressed while that is showing.
 */
export function DiscussionButton({
  count,
  label,
  pressed,
  size = "icon-xs",
  className,
  ...props
}: Omit<ComponentProps<typeof Button>, "children" | "variant" | "aria-label" | "aria-pressed"> & {
  count: number;
  /** What it opens and how many comments, for a screen reader: "Open discussion for Quarterly plan, 7 comments". */
  label: string;
  /** Where it toggles the discussion beside the page: whether that is open. */
  pressed?: boolean;
}) {
  return (
    <Button
      size={size}
      variant={pressed ? "secondary" : "ghost"}
      aria-label={label}
      aria-pressed={pressed}
      className={cn("relative overflow-visible", pressed ? "text-foreground" : "text-muted-foreground hover:text-foreground", className)}
      {...props}
    >
      <MessageSquareIcon />
      {count > 0 ? (
        <span
          aria-hidden
          className="absolute -right-1 -top-1 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-primary px-1 text-3xs font-semibold leading-none tabular-nums text-primary-foreground shadow-sm ring-1 ring-background"
        >
          {count}
        </span>
      ) : null}
    </Button>
  );
}
